import { useEffect, useState } from "react";
import * as T from "../../../utilities/frontendTypes";
import { Address } from "../../../Components/Form/AddressFields";
import textData from "../../../textData.json";
import { DatePickerInput } from "@components/ui/date-picker-input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@components/ui/select";
import { Input } from "@components/ui/input";
import { Textarea } from "@components/ui/textarea";
import { Checkbox } from "@components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@components/ui/radio-group";
import { Label } from "@components/ui/label";
import { toDate } from "date-fns";
import { AlertCircle, Info } from "lucide-react";
import { NumericFormat } from "react-number-format";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@components/ui/tooltip";

interface CompanyInfoSectionProps {
  founderProfile: T.StartupFounderProfile | null;
  mailingAddress: any;
  onSave: (companyInfo: T.CompanyInfo) => void;
  onValidationFail: () => void;
  langState: T.LangState;
  onSaveRef?: React.MutableRefObject<(() => void) | null>;
}

const techOptions = [
  { value: "Advanced Energy Technology", text: "Advanced Energy Technology", frText: "Technologie énergétique avancée" },
  { value: "Advanced Materials and Manufacturing", text: "Advanced Materials and Manufacturing", frText: "Matériaux et fabrication avancés" },
  { value: "Advanced Sensing and Surveillance", text: "Advanced Sensing and Surveillance", frText: "Détection et surveillance avancées" },
  { value: "Advanced Digital Infrastructure", text: "Advanced Digital Infrastructure", frText: "Infrastructure numérique avancée" },
  { value: "Advanced Weapons", text: "Advanced Weapons", frText: "Armes avancées" },
  { value: "Aerospace, Space and Satellite", text: "Aerospace, Space and Satellite", frText: "Aérospatiale, espace et satellites" },
  { value: "Artificial Intelligence and Big Data", text: "Artificial Intelligence and Big Data", frText: "Intelligence artificielle et mégadonnées" },
  { value: "Human-Machine Integration", text: "Human-Machine Integration", frText: "Intégration homme-machine" },
  { value: "Life Science", text: "Life Science", frText: "Sciences de la vie" },
  { value: "Quantum Science", text: "Quantum Science", frText: "Science quantique" },
  { value: "Robotics and Autonomous Systems", text: "Robotics and Autonomous Systems", frText: "Robotique et systèmes autonomes" },
  { value: "Securetech", text: "Securetech", frText: "Securetech" },
  { value: "Other", text: "Other", frText: "Autre" },
];

const provinceOptions = [
  {value: "AB", text: "Alberta", frText: "Alberta"},
  { value: "BC", text: "British Columbia", frText: "Colombie-Britannique" },
  { value: "MB", text: "Manitoba", frText: "Manitoba" },
  { value: "NB", text: "New Brunswick", frText: "Nouveau-Brunswick" },
  { value: "NL", text: "Newfoundland and Labrador", frText: "Terre-Neuve-et-Labrador" },
  { value: "NT", text: "Northwest Territories", frText: "Territoires du Nord-Ouest" },
  { value: "NS", text: "Nova Scotia", frText: "Nouvelle-Écosse" },
  { value: "NU", text: "Nunavut", frText: "Nunavut" },
  { value: "ON", text: "Ontario", frText: "Ontario" },
  { value: "PE", text: "Prince Edward Island", frText: "Île-du-Prince-Édouard" },
  { value: "QC", text: "Quebec", frText: "Québec" },
  { value: "SK", text: "Saskatchewan", frText: "Saskatchewan" },
  { value: "YT", text: "Yukon", frText: "Yukon" }
];

const requiredFieldErrorMessage = (langState: T.LangState) => (
  <p className="flex items-center gap-1 ml-2 px-2 font-semibold text-danger text-sm">
    <AlertCircle className="size-4" />{langState === "fr" ? "Champ obligatoire" : "Required Field"}{/* TODO-FR-REVIEW */}
  </p>
);

function CompanyInfoSection({ founderProfile, mailingAddress, onSave, onValidationFail, langState, onSaveRef }: CompanyInfoSectionProps) {
  const [companyName, setCompanyName] = useState("")
  const [companyInfoStateObj, setCompanyInfoStateObj] = useState<{ [key: string]: any }>({});
  const [requireIndusrtyOther, setRequireIndusrtyOther] = useState(false)
  const [tech, setTech] = useState<string[]>([]);
  const [requireTechOther, setRequireTechOther] = useState(false)
  const [hasDualUseApplications, setHasDualUseApplications] = useState<boolean | null>(null)
  const [revFromTech, setRevFromTech] = useState<boolean | null>(null)
  const [regDate, setRegDate] = useState<Date | null>(null)
  const [provincialRegistered, setProvincialRegistered] = useState<boolean | null>(null)
  const [federalRegistered, setFederalRegistered] = useState<boolean | null>(null)
  const [province, setProvince] = useState<string[]>([]);
  const [errorArray, setErrorArray] = useState<string[]>([]);
  const [bnNotNine, setBnNotNine] = useState(false)
  const [cadGreaterThanGlobal, setCadGreaterThanGlobal] = useState(false)
  const [submittedSectionError, setSubmittedSectionError] = useState(false)

  // Initialize state from founderProfile
  useEffect(() => {
    const initialProfile = founderProfile;
    if (!initialProfile?.companyInfo) return;

    setCompanyName(initialProfile.companyInfo.basicInfo?.operatingName || "")

    if (initialProfile.companyInfo.basicInfo !== null && initialProfile.companyInfo.registration !== null && initialProfile.companyInfo.mailingAddress) {
      setCompanyInfoStateObj({...initialProfile.companyInfo.basicInfo, ...initialProfile.companyInfo.registration})
      if (initialProfile.companyInfo.registration.date) {
        setRegDate(toDate(initialProfile.companyInfo.registration.date))
      }
      if (initialProfile.companyInfo.registration.registeredFederally === false || initialProfile.companyInfo.registration.registeredFederally) {
        setFederalRegistered(initialProfile.companyInfo.registration.registeredFederally)
      }
      if (initialProfile.companyInfo.registration.registeredProvincially === false || initialProfile.companyInfo.registration.registeredProvincially) {
        setProvincialRegistered(initialProfile.companyInfo.registration.registeredProvincially)
      }
      if (initialProfile.companyInfo.registration.province) {
        setProvince(initialProfile.companyInfo.registration.province)
      }
      if (initialProfile.companyInfo.basicInfo.tech) {
        setTech(initialProfile.companyInfo.basicInfo.tech)
      }
      if (initialProfile.companyInfo.basicInfo.revFromTech === false || initialProfile.companyInfo.basicInfo.revFromTech) {
        setRevFromTech(initialProfile.companyInfo.basicInfo.revFromTech)
      }
      if (initialProfile.companyInfo.basicInfo.hasDualUseApplications === false || initialProfile.companyInfo.basicInfo.hasDualUseApplications) {
        setHasDualUseApplications(initialProfile.companyInfo.basicInfo.hasDualUseApplications)
      }
      if (initialProfile.companyInfo.basicInfo.tech?.includes("Other")) {
        setRequireTechOther(true)
      }
      if (initialProfile.companyInfo.basicInfo.industry?.includes("Other")) {
        setRequireIndusrtyOther(true)
      }
    }
    // eslint-disable-next-line
  }, [founderProfile]);

  // Error-clearing effects
  useEffect(() => {
    if (errorArray.includes("registeredFederally")) {
      setErrorArray(prev => prev.filter(item => item !== "registeredFederally"));
    }
  }, [federalRegistered])

  useEffect(() => {
    if (errorArray.includes("registeredProvincially")) {
      setErrorArray(prev => prev.filter(item => item !== "registeredProvincially"));
    }
  }, [provincialRegistered])

  useEffect(() => {
    if (errorArray.includes("revFromTech")) {
      setErrorArray(prev => prev.filter(item => item !== "revFromTech"));
    }
  }, [revFromTech])

  useEffect(() => {
    if (errorArray.includes("tech") && tech.length > 0) {
      setErrorArray(prev => prev.filter(item => item !== "tech"));
    }
  }, [tech])

  useEffect(() => {
    const firstError = document.querySelector('.reqFieldError');
    if (firstError) {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [submittedSectionError])

  const handleRegDateChange = (date: Date | null | undefined) => {
    if (date) {
      setRegDate(date);
      setErrorArray(prev => prev.filter(item => item !== "date"));
    }
  };

  const handleCompanyInfoChangeString = (event: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>) => {
    const { id, value } = event.target;
    if (errorArray.includes(id)) {
      setErrorArray(prev => prev.filter(item => item !== id));
    }
    let modifyValue: string | number | boolean = value
    if (id === "number" && modifyValue !== "" || id === "numFullTimeEmployees" && modifyValue !== "" || id === "numFullTimeEmployeesCan" && modifyValue !== ""  || id === "revenueOverTwelveMonths" && modifyValue !== "" || id === "patentsGranted" && modifyValue !== "" ) {
      modifyValue = parseInt(value)
      if (id === "numFullTimeEmployees" && companyInfoStateObj.numFullTimeEmployeesCan !== undefined || id === "numFullTimeEmployees" && companyInfoStateObj.numFullTimeEmployeesCan !== null ) {
        const numFullTimeEmployeesCan = parseInt(companyInfoStateObj.numFullTimeEmployeesCan)
        setCadGreaterThanGlobal(numFullTimeEmployeesCan > modifyValue)
      }
      if (id === "numFullTimeEmployeesCan" && companyInfoStateObj.numFullTimeEmployees !== undefined || id === "numFullTimeEmployeesCan" && companyInfoStateObj.numFullTimeEmployees !== null ) {
        const numFullTimeEmployees = parseInt(companyInfoStateObj.numFullTimeEmployees)
        setCadGreaterThanGlobal(numFullTimeEmployees < modifyValue)
      }
    }

    if (id === "companyName") {
      setCompanyName(value);
    }
    setCompanyInfoStateObj(prev => ({
      ...prev,
      [id]: modifyValue,
    }));
  };

  const handleCompanyInfoChangeSelect = (id: string, value: string) => {
    if (errorArray.includes(id)) {
      setErrorArray(prev => prev.filter(item => item !== id));
    }
    if (id === "industry" && value === "Other") {
      setRequireIndusrtyOther(true)
    } else if (id === "industry" && value !== "Other") {
      if (errorArray.includes("industryOther")) {
        setErrorArray(prev => prev.filter(item => item !== "industryOther"));
      }
      setRequireIndusrtyOther(false)
    }
    setCompanyInfoStateObj(prev => ({
      ...prev,
      [id]: value,
    }));
  };

  const handleTechCheckboxChange = (value: string) => {
    setTech(prev => {
      const updated = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];
      setRequireTechOther(updated.includes("Other"));
      return updated;
    });
  };

  const handleProvinceCheckboxChange = (value: string) => {
    setProvince(prev => {
      const updated = prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value];
      return updated;
    });
  };

  const companyInfoErrorCheck = () => {
    let i = 0
    if (!companyInfoStateObj.legalName) {
      setErrorArray(prev => [...prev, "legalName"])
      i++
    }
    if (!companyInfoStateObj.description) {
      setErrorArray(prev => [...prev, "description"])
      i++
    }
    if (!companyInfoStateObj.industry) {
      setErrorArray(prev => [...prev, "industry"])
      i++
    }
    if (!companyInfoStateObj.websiteURL) {
      setErrorArray(prev => [...prev, "websiteURL"])
      i++
    }
    if (!companyInfoStateObj.linkedInURL) {
      setErrorArray(prev => [...prev, "linkedInURL"])
      i++
    }
    if (tech.length === 0) {
      setErrorArray(prev => [...prev, "tech"])
      i++
    }
    if (requireTechOther) {
      if (!companyInfoStateObj.techOther) {
        setErrorArray(prev => [...prev, "techOther"])
        i++
      }
    }
    if (companyInfoStateObj.numFullTimeEmployees !== 0 && !companyInfoStateObj.numFullTimeEmployees) {
      setErrorArray(prev => [...prev, "numFullTimeEmployees"])
      i++
    }
    if (companyInfoStateObj.numFullTimeEmployeesCan !== 0 && !companyInfoStateObj.numFullTimeEmployeesCan) {
      setErrorArray(prev => [...prev, "numFullTimeEmployeesCan"])
      i++
    }
    if (revFromTech === null) {
      setErrorArray(prev => [...prev, "revFromTech"])
      i++
    }
    if (hasDualUseApplications === null) {
      setErrorArray(prev => [...prev, "hasDualUseApplications"])
      i++
    }
    if (requireIndusrtyOther) {
      if (!companyInfoStateObj.industryOther) {
        setErrorArray(prev => [...prev, "industryOther"])
        i++
      }
    }
    if (!companyInfoStateObj.techReadiness) {
      setErrorArray(prev => [...prev, "techReadiness"])
      i++
    }
    if (companyInfoStateObj.patentsGranted !== 0 && !companyInfoStateObj.patentsGranted) {
      setErrorArray(prev => [...prev, "patentsGranted"])
      i++
    }
    if (regDate === null) {
      setErrorArray(prev => [...prev, "date"])
      i++
    }
    if (federalRegistered === null) {
      setErrorArray(prev => [...prev, "registeredFederally"])
      i++
    }
    if (provincialRegistered === null) {
      setErrorArray(prev => [...prev, "registeredProvincially"])
      i++
    }
    if (companyInfoStateObj.revenueOverTwelveMonths !== 0 && !companyInfoStateObj.revenueOverTwelveMonths) {
      setErrorArray(prev => [...prev, "revenueOverTwelveMonths"])
      i++
    }
    if (!companyInfoStateObj.number) {
      setErrorArray(prev => [...prev, "number"])
      i++
    }
    if (provincialRegistered && province.length === 0) {
      setErrorArray(prev => [...prev, "province"])
      i++
    }
    if (i === 0 && !cadGreaterThanGlobal) {
      // Assemble companyInfo and pass to parent
      const address = mailingAddress as unknown
      const assembledCompanyInfo: T.CompanyInfo = {
        basicInfo: {
          operatingName: companyName,
          legalName: companyInfoStateObj.legalName as string,
          description: companyInfoStateObj.description as string,
          industry: companyInfoStateObj.industry as string,
          websiteURL: companyInfoStateObj.websiteURL as string,
          linkedInURL: companyInfoStateObj.linkedInURL as string,
          revenueOverTwelveMonths: companyInfoStateObj.revenueOverTwelveMonths as number,
          tech: tech as string[],
          numFullTimeEmployees: companyInfoStateObj.numFullTimeEmployees as number,
          numFullTimeEmployeesCan: companyInfoStateObj.numFullTimeEmployeesCan as number,
          revFromTech: revFromTech as boolean,
          hasDualUseApplications: hasDualUseApplications as boolean,
          techReadiness: companyInfoStateObj.techReadiness as string,
          patentsGranted: companyInfoStateObj.patentsGranted as number
        },
        registration: {
          number: companyInfoStateObj.number,
          date: regDate as Date,
          province: province as T.ProvinceCode[],
          registeredFederally: federalRegistered as boolean,
          registeredProvincially: provincialRegistered as boolean
        },
        mailingAddress: address as Address
      }
      if (requireIndusrtyOther && assembledCompanyInfo.basicInfo) {
        assembledCompanyInfo.basicInfo.industryOther = companyInfoStateObj.industryOther as string
      }
      if (requireTechOther && assembledCompanyInfo.basicInfo) {
        assembledCompanyInfo.basicInfo.techOther = companyInfoStateObj.techOther as string
      }
      onSave(assembledCompanyInfo)
    } else {
      setSubmittedSectionError(!submittedSectionError)
      onValidationFail()
    }
  }

  // Expose save trigger to parent for sticky header
  if (onSaveRef) onSaveRef.current = () => companyInfoErrorCheck();

  const bnNumLink = document.querySelector('a[href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/registering-your-business/you-need-a-business-number-a-program-account.html"]');
  if (bnNumLink) {
    bnNumLink.classList.add('underline');
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-wrap w-full items-center">
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="companyName">{langState === "fr" ? "Nom opérationnel" : "Operating Name"}</label>
              {errorArray.includes("companyName") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input onChange={handleCompanyInfoChangeString} type="text" placeholder={langState === "fr" ? "Saisir le nom opérationnel" : "Enter operating name"} id="companyName" className="w-full" name="companyName" value={companyName} />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="legalName">{langState === "fr" ? "Nom légal" : "Legal Name"}</label>
              {errorArray.includes("legalName") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input type="text" placeholder={langState === "fr" ? "Saisir le nom légal" : "Enter legal name"} id="legalName" name="legalName"
            className="w-full"
            value={companyInfoStateObj.legalName} onChange={handleCompanyInfoChangeString} />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="description">{langState === "fr" ? "Description de l'entreprise" : "Description"}</label>
              {errorArray.includes("description") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Textarea placeholder={langState === "fr" ? "Décrivez votre entreprise" : "Describe your company"} id="description" name="description"
            className="w-full min-h-[100px]"
            value={companyInfoStateObj.description} onChange={handleCompanyInfoChangeString} />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="industry">{langState === "fr" ? "Industrie" : "Industry"}</label>
              {errorArray.includes("industry") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Select value={companyInfoStateObj.industry || undefined} onValueChange={(v) => handleCompanyInfoChangeSelect("industry", v)}>
              <SelectTrigger className="w-full border-border">
                <SelectValue placeholder={langState === "fr" ? "Veuillez faire une sélection" : "Please make a selection"} />
              </SelectTrigger>
              <SelectContent className="max-w-[min(600px,90vw)]">
                <SelectItem value="Agriculture">
                  {langState === "fr" ? "Agriculture" : "Agriculture"}
                </SelectItem>
                <SelectItem value="Advanced Materials & Manufacturing: Excludes specifically forestry, agriculture, mining, clean technologies and life sciences">
                  {langState === "fr" ? "Matériaux d'avant-garde et fabrication Exclut spécifiquement la sylviculture, l'agriculture, l'exploitation minière, les technologies propres et les sciences de la vie." : "Advanced Materials & Manufacturing: Excludes specifically forestry, agriculture, mining, clean technologies and life sciences"}
                </SelectItem>
                <SelectItem value="Clean Tech: Technologies that protect and/or increase efficient utilization of land, water and air resources">
                  {langState === "fr" ? "Tech. propres : technologies qui protègent et/ou augmentent l'utilisation efficace des ressources de la terre, de l'eau et de l'air" : "Clean Tech: Technologies that protect and/or increase efficient utilization of land, water and air resources"}
                </SelectItem>
                <SelectItem value="Consumer Retail">
                  {langState === "fr" ? "Consommation de détail" : "Consumer Retail"}
                </SelectItem>
                <SelectItem value="Digital Media & Telecommunications: Digital applications and content and information and communications technologies. Excludes healthcare, agriculture, forestry, mining, financial services, education, social innovation, culture/recreation and energy-related solutions">
                  {langState === "fr" ? "Médias numériques et télécommunications Applications numériques et technologies de l'information et de communication Exclut les soins de santé, l'agriculture, la sylviculture, l'exploitation minière, les services financiers, l'éducation, l'innovation sociale, la culture et loisirs, et les solutions liées à l'énergie." : "Digital Media & Telecommunications: Digital applications and content and information and communications technologies. Excludes healthcare, agriculture, forestry, mining, financial services, education, social innovation, culture/recreation and energy-related solutions"}
                </SelectItem>
                <SelectItem value="Education">
                  {langState === "fr" ? "Éducation" : "Education"}
                </SelectItem>
                <SelectItem value="Financial Services">
                  {langState === "fr" ? "Services financiers" : "Financial Services"}
                </SelectItem>
                <SelectItem value="Food & Beverage">
                  {langState === "fr" ? "Restauration/production d'aliments et boissons" : "Food & Beverage"}
                </SelectItem>
                <SelectItem value="Forestry">
                  {langState === "fr" ? "Foresterie" : "Forestry"}
                </SelectItem>
                <SelectItem value="Life Sciences & Health">
                  {langState === "fr" ? "Sciences de la vie et de la santé" : "Life Sciences & Health"}
                </SelectItem>
                <SelectItem value="Mining, Oil & Gas: Includes extractive activities that would not otherwise fall under clean technologies">
                  {langState === "fr" ? "Exploitation minière, pétrole et gaz : Comprend les activités d'extraction qui ne relèveraient pas autrement des technologies propres" : "Mining, Oil & Gas: Includes extractive activities that would not otherwise fall under clean technologies"}
                </SelectItem>
                <SelectItem value="Tourism & Culture: Includes entertainment and sports">
                  {langState === "fr" ? "Culture et tourisme : comprend le divertissement et le sport" : "Tourism & Culture: Includes entertainment and sports"}
                </SelectItem>
                <SelectItem value="Transportation">
                  {langState === "fr" ? "Transport" : "Transportation"}
                </SelectItem>
                <SelectItem value="Other">
                  {langState === "fr" ? "Autre" : "Other"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {requireIndusrtyOther &&
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="industryOther">{langState === "fr" ? "Secteur d'activité (Autre, veuillez préciser)" : "Industry (Other, please specify)"}</label>
              {errorArray.includes("industryOther") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input type="text" id="industryOther" name="industryOther"
            className="w-full"
            value={companyInfoStateObj.industryOther || ""} onChange={handleCompanyInfoChangeString} />
          </div>
        </div>
        }
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2 w-full">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="tech">{langState === "fr" ? "Technologie" : "Technology"}</label>
              {errorArray.includes("tech") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="flex flex-wrap" id="tech">
            {techOptions.map((item) => (
              <div className="w-1/2" key={item.value}>
                <div className="flex items-center gap-2 my-1.5">
                  <Checkbox
                    id={`tech-${item.value}`}
                    checked={tech.includes(item.value)}
                    onCheckedChange={() => handleTechCheckboxChange(item.value)}
                  />
                  <Label htmlFor={`tech-${item.value}`} className="cursor-pointer font-normal">{langState === "fr" && item.frText ? item.frText : item.text}</Label>
                </div>
              </div>
            ))}
          </div>
        </div>
        {requireTechOther &&
        <div className="flex flex-col mb-6 w-full">
        <div className="flex mb-2">
          <div className="flex">
            <label className="text-base font-medium text-text" htmlFor="techOther">{langState === "fr" ? "Technologie (Autre, veuillez préciser)" : "Tech (Other, please specify)"}</label>
            {errorArray.includes("techOther") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
        </div>
        <div className="relative flex items-center">
          <Input type="text" id="techOther" name="techOther"
          className="w-full"
          value={companyInfoStateObj.techOther} onChange={handleCompanyInfoChangeString} />
        </div>
      </div>
        }
        <div className="flex flex-col mb-6 w-full">
          <div>
            <p className="mb-2">{langState === "fr" ? "Avez-vous confirmé que votre technologie a des applications potentielles à double usage (c.-à-d. civiles et militaires)?" : "Have you confirmed that your technology has potential dual-use (i.e. both civilian and military) applications?"}</p>
            <p className="bodySmaller mb-2">{langState === "fr" ? "Veuillez noter qu'une réponse « oui » à cette question incitera notre équipe à examiner et valider votre réponse de manière plus approfondie si vous atteignez l'étape suivante du processus de candidature." : "Please note that indicating \"yes\" to this question will prompt our team to further investigate and validate your response if you are successful in reaching the next stage of the application process."}</p>
            <div className="mb-2">
            {errorArray.includes("hasDualUseApplications") &&
              requiredFieldErrorMessage(langState)
            }
            </div>
          </div>
          <div className="flex gap-4">
            <RadioGroup className="flex gap-4" value={hasDualUseApplications === null ? undefined : hasDualUseApplications ? "yes" : "no"} onValueChange={(v) => setHasDualUseApplications(v === "yes")}>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="yes" id="hasDualUseApplications-yes" />
                <Label htmlFor="hasDualUseApplications-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
              </div>
              <div className="flex items-center gap-2">
                <RadioGroupItem value="no" id="hasDualUseApplications-no" />
                <Label htmlFor="hasDualUseApplications-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="websiteURL">{langState === "fr" ? "URL du site Web. Veuillez entrer « S.O. » si votre entreprise n'a pas de site Web." : "Website URL. Please enter \"N/A\" if your company does not have a Website."}</label>
              {errorArray.includes("websiteURL") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input type="text" id="websiteURL" placeholder="https://" name="websiteURL"
            className="w-full"
            value={companyInfoStateObj.websiteURL} onChange={handleCompanyInfoChangeString} />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <label className="text-base font-medium text-text" htmlFor="linkedInURL">{langState === "fr" ? "URL LinkedIn de l'entreprise. Veuillez entrer « S.O. » si votre entreprise n'a pas de profil LinkedIn." : "Company LinkedIn URL. Please enter \"N/A\" if your company does not have a LinkedIn profile."}</label>
              <p className="bodySmaller mb-2">{langState === "fr" ? "Veuillez inclure une entrée uniquement s'il s'agit d'une page LinkedIn d'entreprise; aucun profil personnel n'est requis" : "Please only include an entry if it is a company LinkedIn page, no personal profiles are needed"}{/* TODO-FR-REVIEW */}</p>
            </div>
          </div>
          <div className="relative flex items-center">
            <Input type="text" id="linkedInURL" placeholder="https://" name="linkedInURL"
            className="w-full"
            value={companyInfoStateObj.linkedInURL || ""} onChange={handleCompanyInfoChangeString} />
            {errorArray.includes("linkedInURL") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <label className="text-base font-medium text-text" htmlFor="numFullTimeEmployees">{langState === "fr" ? "Votre entreprise compte combien d'employés à temps plein dans le monde (y compris le Canada)?" : "What is the number of full-time employees at your company globally (including Canada)?"}</label>
              {errorArray.includes("numFullTimeEmployees") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input type="text" id="numFullTimeEmployees" name="numFullTimeEmployees"
            className="w-full"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder={langState === "fr" ? "Chiffres uniquement" : "Enter numbers only"}
            onKeyDown={(e) => {
              const allowedKeys = [
                'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'
              ];
              const isNumberKey = /^\d$/.test(e.key);
              if (!isNumberKey && !allowedKeys.includes(e.key)) {
                e.preventDefault();
              }
            }}
            onChange={(e) => {
              const value = e.target.value;
              if (value === "" || /^\d*$/.test(value)) {
                handleCompanyInfoChangeString(e)
              }
            }}
            value={companyInfoStateObj.numFullTimeEmployees}  />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <label className="text-base font-medium text-text" htmlFor="numFullTimeEmployeesCan">{langState === "fr" ? "Votre entreprise compte combien d'employés à temps plein au Canada?" : "What is the number of full-time employees at your company in Canada?"}</label>
              {errorArray.includes("numFullTimeEmployeesCan") &&
                requiredFieldErrorMessage(langState)
              }
              {cadGreaterThanGlobal &&
                <p className="flex items-center gap-1 ml-2 px-2 font-semibold text-cError reqFieldError" style={{opacity: "1", transform: "none"}}><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="circle-exclamation" className="svg-inline--fa fa-circle-exclamation " role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="currentColor" d="M256 512A256 256 0 1 0 256 0a256 256 0 1 0 0 512zm0-384c13.3 0 24 10.7 24 24V264c0 13.3-10.7 24-24 24s-24-10.7-24-24V152c0-13.3 10.7-24 24-24zM224 352a32 32 0 1 1 64 0 32 32 0 1 1 -64 0z"></path></svg>{langState === "fr" ? "Le nombre d'employés canadiens ne peut pas dépasser le nombre total d'employés" : "Number of Canadian Employees may not exceed number of total Employees"}{/* TODO-FR-REVIEW */}</p>
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input
              type="text"
              id="numFullTimeEmployeesCan"
              name="numFullTimeEmployeesCan"
              className="w-full"
              inputMode="numeric"
              pattern="[0-9]*"
              placeholder={langState === "fr" ? "Chiffres uniquement" : "Enter numbers only"}
              value={companyInfoStateObj.numFullTimeEmployeesCan}
              onKeyDown={(e) => {
                const allowedKeys = [
                  'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'
                ];
                const isNumberKey = /^\d$/.test(e.key);
                if (!isNumberKey && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                handleCompanyInfoChangeString(e);
              }}
            />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <label className="text-base font-medium text-text" htmlFor="revenueOverTwelveMonths">{langState === "fr" ? "Indiquez le revenu total de votre entreprise (en CAD) au cours de votre dernier exercice financier." : "Indicate your company's total revenue (in $CAD) during your most recent fiscal year."}</label>
              {errorArray.includes("revenueOverTwelveMonths") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
        <div className="relative flex items-center">
            <NumericFormat
              customInput={Input}
              thousandSeparator=","
              allowNegative={false}
              decimalScale={0}
              className="w-full"
              placeholder="$0"
              id="revenueOverTwelveMonths"
              value={companyInfoStateObj.revenueOverTwelveMonths ?? ""}
              onValueChange={(values) => {
                const syntheticEvent = { target: { value: values.value, id: "revenueOverTwelveMonths" } };
                handleCompanyInfoChangeString(syntheticEvent as any);
              }}
              isAllowed={(values) => !values.value || values.value.length <= 15}
            />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex">
            <span className="mb-2">{langState === "fr" ? "Votre principale source de revenus (ou source prévue s'il s'agit de pré-revenus) provient-elle de la technologie en cours de développement et non de services ou de conseils?" : "Is your primary source of revenue (or intended source, if pre-revenue) derived from the technology being developed and not from services or consulting?"}</span>
            {errorArray.includes("revFromTech") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
          <RadioGroup className="flex gap-4" value={revFromTech === null ? undefined : revFromTech ? "yes" : "no"} onValueChange={(v) => setRevFromTech(v === "yes")}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="revFromTech-yes" />
              <Label htmlFor="revFromTech-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="revFromTech-no" />
              <Label htmlFor="revFromTech-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="techReadiness">{langState === "fr" ? "Quel est le niveau de maturité technologique (TRL) de votre produit le plus abouti?" : "What is the Technology Readiness Level of your most mature product?"}</label>
              {errorArray.includes("techReadiness") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Select value={companyInfoStateObj.techReadiness || undefined} onValueChange={(v) => handleCompanyInfoChangeSelect("techReadiness", v)}>
              <SelectTrigger className="w-full border-border">
                <SelectValue placeholder={langState === "fr" ? "Veuillez faire une sélection" : "Please make a selection"} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="trl1">
                  {langState === "fr" ? "TRL 1 : Principes fondamentaux observés et rapportés" : "TRL 1: Basic principles observed and reported"}
                </SelectItem>
                <SelectItem value="trl2">
                  {langState === "fr" ? "TRL 2 : Concept technologique et/ou application formulés" : "TRL 2: Technology concept and/or application formulated"}
                </SelectItem>
                <SelectItem value="trl3">
                  {langState === "fr" ? "TRL 3 : Preuve de concept analytique et expérimentale" : "TRL 3: Analytical and experimental proof of concept"}
                </SelectItem>
                <SelectItem value="trl4">
                  {langState === "fr" ? "TRL 4 : Composant et/ou validation en environnement de laboratoire" : "TRL 4: Component and/or validation in a laboratory environment"}
                </SelectItem>
                <SelectItem value="trl5">
                  {langState === "fr" ? "TRL 5 : Composant et/ou validation dans un environnement simulé" : "TRL 5: Component and/or validation in a simulated environment"}
                </SelectItem>
                <SelectItem value="trl6">
                  {langState === "fr" ? "TRL 6 : Démonstration d'un modèle ou prototype de système ou sous-système dans un environnement simulé (c.-à-d. un prototype prêt à l'emploi)" : "TRL 6: System/subsystem model or prototype demonstration in a simulated environment (i.e. prototype ready)"}
                </SelectItem>
                <SelectItem value="trl7">
                  {langState === "fr" ? "TRL 7 : Prototype prêt à être démontré dans un environnement opérationnel approprié (c.-à-d. prêt pour une version MVP [Produit Minimum Viable])" : "TRL 7: Prototype ready for demonstration in an appropriate operational environment (i.e. MVP ready)"}
                </SelectItem>
                <SelectItem value="trl8">
                  {langState === "fr" ? "TRL 8 : Technologie concrète mise au point et qualifiée par des essais et des démonstrations (c.-à-d. projets pilotes déployés)" : "TRL 8: Actual technology completed and qualified through tests and demonstrations (i.e. pilots deployed)"}
                </SelectItem>
                <SelectItem value="trl9">
                  {langState === "fr" ? "TRL 9 : Technologie éprouvée grâce à un déploiement réussi en situation opérationnelle" : "TRL 9: Actual technology proven through successful deployment in an operational setting"}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <label className="text-base font-medium text-text" htmlFor="patentsGranted">{langState === "fr" ? "Combien de brevets (y compris les dessins et modèles industriels) ont été accordés à votre entreprise?" : "How many patents (including industrial designs) have been granted to your company"}</label>
              {errorArray.includes("patentsGranted") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <Input type="text" id="patentsGranted" name="patentsGranted"
              className="w-full"
              inputMode="numeric"
              pattern="[0-9]*"
              onKeyDown={(e) => {
                const allowedKeys = [
                  'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'
                ];
                const isNumberKey = /^\d$/.test(e.key);
                if (!isNumberKey && !allowedKeys.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onChange={(e) => {
                const value = e.target.value;
                if (value === "" || /^\d*$/.test(value)) {
                  handleCompanyInfoChangeString(e)
                }
              }}
            value={companyInfoStateObj.patentsGranted}  />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <label className="text-base font-medium text-text" htmlFor="number">{langState === "fr" ? "Votre numéro d'entreprise à 9 chiffres de l'Agence du revenu du Canada" : "Your Canada Revenue Agency 9-Digit Business Number"}</label>{/* TODO-FR-REVIEW */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Info className="size-4 text-muted-foreground cursor-help shrink-0" />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="max-w-sm text-sm">
                      {langState === "fr" ? "Le numéro d'entreprise (NE), attribué à toutes les entreprises constituées en société par l'ARC, est un numéro à 9 chiffres unique à une entreprise ou entité juridique. Il est différent du numéro de société à 7 chiffres et ne doit pas contenir de trait d'union. Un NE valide à 9 chiffres est requis pour participer à ÉleverlaPI." : "The business number (BN), assigned to all incorporated businesses by the CRA, is a 9-digit number unique to a business or legal entity. It is different from the 7-digit Corporation Number and should not contain a hyphen. A valid 9-digit BN is required for participation in ElevateIP."}{/* TODO-FR-REVIEW */} <a target="_blank" rel="noopener noreferrer" href="https://www.canada.ca/en/revenue-agency/services/tax/businesses/topics/registering-your-business/you-need-a-business-number-a-program-account.html" className="underline text-primary">{langState === "fr" ? "En savoir plus" : "Learn more"}{/* TODO-FR-REVIEW */}</a>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {errorArray.includes("number") &&
                requiredFieldErrorMessage(langState)
              }
              {bnNotNine &&
                <p className="flex items-center gap-1 ml-2 px-2 font-semibold text-danger text-sm">
                  <AlertCircle className="size-4" />{langState === "fr" ? "Le numéro d'entreprise doit comporter neuf chiffres" : "Business number must be nine digits"}{/* TODO-FR-REVIEW */}
                </p>
              }
            </div>
          </div>
            <div className="relative flex items-center">
              <Input type="text" id="number" name="number"
                className="w-full"
                inputMode="numeric"
                pattern="[0-9]*"
                onKeyDown={(e) => {
                  const allowedKeys = [
                    'Backspace', 'ArrowLeft', 'ArrowRight', 'Tab', 'Delete'
                  ];
                  const isNumberKey = /^\d$/.test(e.key);
                  if (!isNumberKey && !allowedKeys.includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={9}
                onChange={(e) => {
                  // Cap at 9 digits (maxLength alone doesn't cover paste in all browsers)
                  e.target.value = e.target.value.replace(/\D/g, "").slice(0, 9);
                  const value = e.target.value;
                  if (value.length !== 9) {
                    setBnNotNine(true)
                  } else if (value.length === 9) {
                    setBnNotNine(false)
                  }
                  handleCompanyInfoChangeString(e)
                }}
              value={companyInfoStateObj.number}  />
          </div>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2">
            <div className="flex flex-col">
              <label className="text-base font-medium text-text flex flex-col" htmlFor="date">
                {langState === "fr" ? "Date de l'enregistrement" : "Registration Date"}
              </label>
              {errorArray.includes("date") &&
                requiredFieldErrorMessage(langState)
              }
            </div>
          </div>
          <div className="relative flex items-center">
            <DatePickerInput
              className="w-full"
              id={"date"}
              selected={regDate === null ? undefined : regDate}

              showYearDropdown
              yearDropdownItemNumber={115}
              onChange={handleRegDateChange}
              maxDate={new Date()}
            />
          </div>
         </div>
         <div className="flex flex-col mb-6 w-full">
          <div className="flex">
            <p className="mb-2">{langState === "fr" ? "Votre entreprise est-elle enregistrée au niveau fédéral?" : "Is your company registered Federally?"}</p>
              {errorArray.includes("registeredFederally") &&
              <p className="mb-2">
                {requiredFieldErrorMessage(langState)}
                </p>
              }
            </div>
          <RadioGroup className="flex gap-4" value={federalRegistered === null ? undefined : federalRegistered ? "yes" : "no"} onValueChange={(v) => setFederalRegistered(v === "yes")}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="federal-yes" />
              <Label htmlFor="federal-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="federal-no" />
              <Label htmlFor="federal-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
            </div>
          </RadioGroup>
        </div>
        <div className="flex flex-col mb-6 w-full">
          <div className="flex">
            <p className="mb-2">{langState === "fr" ? "Votre entreprise est-elle enregistrée dans une province ou dans un territoire?" : "Is your company registered in any Provinces or Territories?"}</p>
              {errorArray.includes("registeredProvincially") &&
                requiredFieldErrorMessage(langState)
              }
          </div>
          <RadioGroup className="flex gap-4" value={provincialRegistered === null ? undefined : provincialRegistered ? "yes" : "no"} onValueChange={(v) => setProvincialRegistered(v === "yes")}>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="yes" id="provincial-yes" />
              <Label htmlFor="provincial-yes" className="cursor-pointer font-normal">{langState === "fr" ? "Oui" : "Yes"}</Label>
            </div>
            <div className="flex items-center gap-2">
              <RadioGroupItem value="no" id="provincial-no" />
              <Label htmlFor="provincial-no" className="cursor-pointer font-normal">{langState === "fr" ? "Non" : "No"}</Label>
            </div>
          </RadioGroup>
        </div>
        {provincialRegistered &&
          <div className="flex flex-col mb-6 w-full">
          <div className="flex mb-2 w-full">
            <div className="flex">
              <label className="text-base font-medium text-text" htmlFor="province">{langState === "fr" ? "Sélectionnez les provinces et territoires enregistrés" : "Select Registered Provinces and Territories"}</label>{/* TODO-FR-REVIEW */}
            </div>
            {errorArray.includes("province") &&
              requiredFieldErrorMessage(langState)
            }
          </div>
          <div className="flex flex-wrap" id="province">
            {provinceOptions.map((item) => (
              <div className="w-1/2" key={item.value}>
                <div className="flex items-center gap-2 my-1.5">
                  <Checkbox
                    id={`province-${item.value}`}
                    checked={province.includes(item.value)}
                    onCheckedChange={() => handleProvinceCheckboxChange(item.value)}
                  />
                  <Label htmlFor={`province-${item.value}`} className="cursor-pointer font-normal">
                    {langState === "fr" ? item.frText : item.text}
                  </Label>
                </div>
              </div>
            ))}
          </div>
        </div>
        }
      </div>
    </div>
  );
}

export default CompanyInfoSection;
